import { cn, Switch, Text } from '@risksmart-app/atomic-ui';
import { Controller, useFormContext } from 'react-hook-form';

import {
  type FieldEditorValues,
  isOptionFieldType,
  supportsDefaultValue,
} from '../config';
import { FormSelectField, FormTextField } from './FormFields';
import { OptionsEditor } from './OptionsEditor';

export const FieldEditorConfigFields = ({
  disableFieldType,
  disableFieldName,
  fieldTypeOptions,
  fieldTypeDisabledReason,
  originalLabel,
}: {
  disableFieldType?: boolean;
  disableFieldName?: boolean;
  fieldTypeOptions: { value: string; label: string }[];
  fieldTypeDisabledReason?: string;
  originalLabel?: string;
}) => {
  const { watch, control } = useFormContext<FieldEditorValues>();
  const fieldType = watch('fieldType');
  const showOptions = isOptionFieldType(fieldType);
  const defaultValueEnabled = watch('defaultValueEnabled');
  const fieldName = watch('fieldName');
  const showOriginalLabel =
    !!originalLabel && originalLabel.length > 0 && fieldName !== originalLabel;

  const fieldNameLabel = showOriginalLabel ? (
    <span className={cn('flex flex-col gap-0.5')}>
      <span>
        {'Field name'}
        <span className={cn('text-destructive ml-0.5')}>{'*'}</span>
      </span>
      <span
        className={cn('text-sm font-normal animate-fade-slide-in')}
        data-testid={'original-label-hint'}
      >
        {`Original label: ${originalLabel}`}
      </span>
    </span>
  ) : (
    'Field name'
  );

  return (
    <div className={cn('flex flex-col gap-4')}>
      <div className={cn('flex flex-col gap-1')}>
        <FormSelectField
          name={'fieldType'}
          label={'Field type'}
          placeholder={'Select a field type'}
          options={fieldTypeOptions}
          disabled={disableFieldType}
        />
        {disableFieldType && fieldTypeDisabledReason ? (
          <Text preset={'body-sm'} className={cn('text-muted-foreground')}>
            {fieldTypeDisabledReason}
          </Text>
        ) : null}
      </div>
      <FormTextField
        name={'fieldName'}
        label={fieldNameLabel}
        placeholder={'Enter field name'}
        disabled={disableFieldName}
        optional={showOriginalLabel ? true : undefined}
      />
      {showOptions && !disableFieldType ? <OptionsEditor /> : null}
      {supportsDefaultValue(fieldType) ? (
        <div className={cn('flex flex-col gap-3')}>
          <Controller
            control={control}
            name={'defaultValueEnabled'}
            render={({ field }) => (
              <label className={cn('flex items-center gap-2 cursor-pointer')}>
                <Text preset={'body-sm'} render={<span />}>
                  {'Set default value'}
                </Text>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  size={'sm'}
                />
              </label>
            )}
          />
          {defaultValueEnabled ? (
            <FormTextField
              name={'defaultValue'}
              label={'Default value'}
              placeholder={'Enter a default value'}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
